<template>
  <img
    v-if="useImg || UseImgIcons.includes(props.name)"
    :class="{ 'icon-spin': props.spin }"
    :src="imgUrl"
    :alt="props.name"
    class="icon-wrapper icon-wrapper-img"
    :style="{ fontSize: props.size, color: props.color }"
  >
  <i
    v-else-if="symbolId"
    class="icon-wrapper"
    :class="{ 'icon-spin': props.spin }"
    :name="props.name"
    :style="{ fontSize: props.size, color: props.color }"
  >
    <svg><use :href="`#${symbolId}`" /></svg>
  </i>
  <i
    v-else-if="svgContent"
    class="icon-wrapper"
    :class="{ 'icon-spin': props.spin }"
    :name="props.name"
    :style="{ fontSize: props.size, color: props.color }"
    v-html="svgContent"
  />
  <i v-else class="icon-wrapper-empty" />
</template>

<script setup lang="ts">
import { envConfig } from '@peerup/host-env'
import { onBeforeUnmount, ref, watch } from 'vue'

import type { SvgIconProps } from './types'

import { EMPTY_SVG, fetchIconContent, getIconMap, getIconMapWithPrivateBrandLogo, loadSprite } from './iconMap'

defineOptions({
  name: 'PeerupSvgIcon',
})

const props = defineProps<SvgIconProps>()

const UseImgIcons = ['copilot']
export type { SvgIconProps }

// 需要拼接 PRIVATE_BRAND_LOGO 的 logo 图标名称（symbol id 与 icons 目录文件名一致）
const LOGO_ICONS = [
  'logo-tm',
  'peerup-logo',
  'app-header-logo',
  'file-icon-logo',
]

const cdnUrlFor = (name: string) => {
  return LOGO_ICONS.includes(name)
    ? getIconMapWithPrivateBrandLogo(name)
    : getIconMap(name)
}

const symbolIdFor = (name: string) => {
  return `icon-${name}${LOGO_ICONS.includes(name) ? envConfig.PRIVATE_BRAND_LOGO : ''}`
}

// img 通道（useImg 显式指定 / UseImgIcons 约定）：src 优先本地（sprite 池原始内容转 data URI），池外回退 CDN
const imgUrl = ref('')

// 组件销毁标志
let isComponentDestroyed = false

// 渲染通道：sprite symbol 优先，池外图标 / sprite 不可用时 CDN 兜底
const symbolId = ref('')
const svgContent = ref('')

const resolve = async () => {
  const name = props.name
  if (!name) {
    symbolId.value = ''
    svgContent.value = ''
    imgUrl.value = ''
    return
  }

  // 通道〇：调用方显式指定 img 渲染——src 本地优先（sprite 原始内容转 data URI），池外回退 CDN 公式
  if (props.useImg || UseImgIcons.includes(name)) {
    let src = ''
    try {
      const { rawIconFor } = await loadSprite()
      const raw = rawIconFor(`./icons/${name}${LOGO_ICONS.includes(name) ? envConfig.PRIVATE_BRAND_LOGO : ''}.svg`)
      if (raw) src = `data:image/svg+xml,${encodeURIComponent(raw)}`
    } catch {
      // sprite 不可用 → CDN
    }
    if (isComponentDestroyed || name !== props.name) return
    imgUrl.value = src || cdnUrlFor(name)
    symbolId.value = ''
    svgContent.value = ''
    return
  }

  // 通道一：sprite（icons 目录内 = symbol 已注入，<use> 零请求渲染）
  try {
    const { ensureSprite } = await loadSprite()
    await ensureSprite()
    if (isComponentDestroyed || name !== props.name) return
    const id = symbolIdFor(name)
    if (document.getElementById(id)) {
      symbolId.value = id
      svgContent.value = ''
      imgUrl.value = ''
      return
    }
  } catch {
    // sprite 加载失败 → 走 CDN 兜底
  }

  // 通道二：CDN 兜底（池外图标 / sprite 不可用 / 应用独立 dev 无域名根）
  if (isComponentDestroyed || name !== props.name) return
  symbolId.value = ''
  imgUrl.value = ''
  try {
    const content = await fetchIconContent(cdnUrlFor(name))
    if (!isComponentDestroyed && name === props.name) {
      svgContent.value = content
    }
  } catch {
    if (!isComponentDestroyed && name === props.name) {
      svgContent.value = EMPTY_SVG
    }
  }
}

const stopWatcher = watch(() => props.name, resolve, { immediate: true })

const cleanup = () => {
  isComponentDestroyed = true
  stopWatcher?.()
  symbolId.value = ''
  svgContent.value = ''
}

onBeforeUnmount(cleanup)
</script>

<style lang="less" scoped>
.icon-wrapper {
  display: inline-block;
  width: fit-content;
  line-height: 1;
  vertical-align: middle;

  &.icon-wrapper-img {
    width: 1em;
  }

  :deep(svg) {
    display: inline-block;
    width: 1em;
    min-width: 100%;
    height: 1em;
    min-height: 100%;
    vertical-align: -2px;
    outline: none;
  }
}

.icon-wrapper-empty {
  display: inline-block;
  width: 1em;
  height: 1em;
}

.icon-spin {
  animation: icon-spin 1s linear infinite;
}
</style>

<style>
@keyframes icon-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
